// assets/js/utils/guest-timezone.js
/**
 * Which time zone(s) is a guest list in, and is it night there?
 *
 * ── Why this exists ──────────────────────────────────────────────────────
 * Quiet hours (server/utils/quiet-hours.js) judged "the guests' end" from
 * country calling codes alone, and mapped every +1 number to Chicago. Reminders
 * the SYSTEM times fire at 09:00 in the HOST's browser zone, so an Israeli
 * host's "1 day before" reminder reached US guests by SMS at about 02:00 ET
 * (pre-mortem finding #10, 2026-10-07) and nothing held it.
 *
 * The +1 zone is in the area code: every US, Canadian and other-NANP code is
 * mapped below to the zone most of its numbers sit in. A US code missing from
 * the table (a new overlay) counts as "somewhere in the US", whose safe window
 * is the one that is daytime from New York to Los Angeles.
 *
 * A list is judged by every zone holding at least a quarter of its recognised
 * numbers, not just the largest: a list that is 70% Israeli and 30% American
 * would otherwise wake the Americans every time. When those zones share no
 * daytime at all (Israel and Hawaii), the largest one alone decides.
 *
 * Dual-environment, like nanp-region.js: the send wizard warns about a time
 * the host picked with the same table the server holds sends with.
 */
(function () {
  // Read when needed: in the browser nanp-region.js may load after this file.
  const nanp = () => ((typeof module !== 'undefined' && module.exports)
    ? require('./nanp-region')
    : (typeof window !== 'undefined' ? window.NanpRegion : null));

  const QUIET_START_HOUR = 22;   // local, inclusive
  const QUIET_END_HOUR = 8;      // local, exclusive
  const RELEASE_HOUR = 9;        // local: a held send goes out from here...
  // ...and only while every guest zone is between RELEASE_HOUR and QUIET_START_HOUR.
  const SIGNIFICANT_SHARE = 0.25;
  const STEP_MS = 15 * 60 * 1000; // every zone offset in use is a multiple of 15 minutes
  const SEARCH_MS = 24 * 60 * 60 * 1000; // never more than one night

  // Calling code -> IANA zone, outside the NANP. Longest prefix wins.
  // Multi-zone countries map to the zone most of their population lives in.
  const DIAL_ZONES = [
    ['972', 'Asia/Jerusalem'], ['971', 'Asia/Dubai'], ['966', 'Asia/Riyadh'], ['974', 'Asia/Qatar'],
    ['965', 'Asia/Kuwait'], ['973', 'Asia/Bahrain'], ['968', 'Asia/Muscat'], ['962', 'Asia/Amman'],
    ['961', 'Asia/Beirut'], ['598', 'America/Montevideo'], ['595', 'America/Asuncion'], ['593', 'America/Guayaquil'],
    ['591', 'America/La_Paz'], ['507', 'America/Panama'], ['506', 'America/Costa_Rica'], ['505', 'America/Managua'],
    ['504', 'America/Tegucigalpa'], ['503', 'America/El_Salvador'], ['502', 'America/Guatemala'], ['351', 'Europe/Lisbon'],
    ['353', 'Europe/Dublin'], ['358', 'Europe/Helsinki'], ['380', 'Europe/Kyiv'], ['234', 'Africa/Lagos'],
    ['254', 'Africa/Nairobi'], ['233', 'Africa/Accra'], ['212', 'Africa/Casablanca'],
    ['7', 'Europe/Moscow'], ['20', 'Africa/Cairo'], ['27', 'Africa/Johannesburg'],
    ['30', 'Europe/Athens'], ['31', 'Europe/Amsterdam'], ['32', 'Europe/Brussels'], ['33', 'Europe/Paris'],
    ['34', 'Europe/Madrid'], ['36', 'Europe/Budapest'], ['39', 'Europe/Rome'], ['40', 'Europe/Bucharest'],
    ['41', 'Europe/Zurich'], ['43', 'Europe/Vienna'], ['44', 'Europe/London'], ['45', 'Europe/Copenhagen'],
    ['46', 'Europe/Stockholm'], ['47', 'Europe/Oslo'], ['48', 'Europe/Warsaw'], ['49', 'Europe/Berlin'],
    ['51', 'America/Lima'], ['52', 'America/Mexico_City'], ['53', 'America/Havana'], ['54', 'America/Argentina/Buenos_Aires'],
    ['55', 'America/Sao_Paulo'], ['56', 'America/Santiago'], ['57', 'America/Bogota'], ['58', 'America/Caracas'],
    ['60', 'Asia/Kuala_Lumpur'], ['61', 'Australia/Sydney'], ['62', 'Asia/Jakarta'], ['63', 'Asia/Manila'],
    ['64', 'Pacific/Auckland'], ['65', 'Asia/Singapore'], ['66', 'Asia/Bangkok'], ['81', 'Asia/Tokyo'],
    ['82', 'Asia/Seoul'], ['86', 'Asia/Shanghai'], ['90', 'Europe/Istanbul'], ['91', 'Asia/Kolkata'],
    ['92', 'Asia/Karachi']
  ].sort((a, b) => b[0].length - a[0].length);

  // +1 area code -> zone. Where a code spans two zones it takes the one most
  // of its numbers are in (850: Pensacola/Panama City Central over
  // Tallahassee; 208: Boise Mountain over the Idaho panhandle). An hour off at
  // the edge of a code still lands inside the 22:00-08:00 margin.
  const AREA_ZONES = {
    'America/New_York': // US Eastern
      '203 475 860 959 302 202 771 239 305 321 324 352 386 407 561 645 656 689 727 728 754 772 786 813 863 904 941 954 ' +
      '229 404 470 478 678 706 762 770 912 943 260 317 463 574 765 812 930 502 606 859 207 227 240 301 410 443 667 ' +
      '339 351 413 508 617 774 781 857 978 231 248 269 313 517 586 616 679 734 810 906 947 989 603 ' +
      '201 551 609 640 732 848 856 862 908 973 212 315 329 332 347 363 516 518 585 607 624 631 646 680 716 718 838 845 914 917 929 934 ' +
      '252 336 472 704 743 828 910 919 980 984 216 220 234 283 326 330 380 419 436 440 513 567 614 740 937 ' +
      '215 223 267 272 412 445 484 570 582 610 717 724 814 835 878 401 803 821 839 843 854 864 423 865 802 ' +
      '276 434 540 571 686 703 757 804 826 948 304 681',
    'America/Chicago': // US Central
      '205 251 256 334 483 659 938 327 479 501 870 448 850 217 224 309 312 331 447 464 618 630 708 730 773 779 815 847 861 872 ' +
      '219 319 515 563 641 712 316 620 785 913 270 364 225 318 337 504 985 218 320 507 612 651 763 924 952 228 601 662 769 ' +
      '235 314 417 557 573 636 660 816 975 308 402 531 701 405 539 572 580 918 605 615 629 731 901 931 ' +
      '210 214 254 281 325 346 361 409 430 432 469 512 682 713 726 737 806 817 830 832 903 936 940 945 956 972 979 ' +
      '262 274 353 414 534 608 715 920',
    'America/Denver': '303 719 720 970 983 208 986 406 505 575 385 435 801 307 915',
    'America/Phoenix': '480 520 602 623 928',
    'America/Los_Angeles':
      '209 213 279 310 323 341 350 357 369 408 415 424 442 510 530 559 562 619 626 628 650 657 661 669 707 714 738 747 ' +
      '760 805 818 820 831 837 840 858 909 916 925 949 951 702 725 775 458 503 541 971 206 253 360 425 509 564',
    'America/Anchorage': '907',
    'Pacific/Honolulu': '808',
    // US territories and the Atlantic-time Caribbean (no daylight saving).
    'America/Puerto_Rico': '787 939 340 246 264 268 284 473 664 721 758 767 784 868 869',
    'Pacific/Guam': '671',
    'Pacific/Saipan': '670',
    'Pacific/Pago_Pago': '684',
    // Canada
    'America/Vancouver': '236 250 257 604 672 778',
    'America/Edmonton': '368 403 568 587 780 825 867',
    'America/Regina': '306 474 639',
    'America/Winnipeg': '204 431 584',
    'America/Toronto':
      '226 249 289 343 365 382 387 416 437 460 519 537 548 613 647 683 705 742 753 807 905 942 ' +
      '263 354 367 418 438 450 468 514 579 581 819 873 600 622 633 638 644 655 677 688',
    'America/Moncton': '428 506',
    'America/Halifax': '782 851 902',
    'America/St_Johns': '709 879',
    // Other NANP countries
    'America/Nassau': '242',
    'America/Panama': '345',
    'Atlantic/Bermuda': '441',
    'America/Grand_Turk': '649',
    'America/Jamaica': '658 876',
    'America/Santo_Domingo': '809 829 849'
  };
  const AREA_ZONE = new Map();
  for (const [zone, codes] of Object.entries(AREA_ZONES)) {
    for (const code of codes.split(/\s+/).filter(Boolean)) AREA_ZONE.set(code, zone);
  }

  // A US number whose area code is not in the table. Its window is the one
  // that is daytime on both coasts.
  const US_WIDE = 'US';
  const US_WIDE_ZONES = ['America/New_York', 'America/Los_Angeles'];

  /** The zone a number is in: an IANA name, US_WIDE, or null when unknown. */
  function zoneForPhone(phone) {
    const digits = String(phone == null ? '' : phone).replace(/\D/g, '');
    if (digits.length < 8) return null;
    if (digits[0] === '1') {
      if (digits.length !== 11) return null;
      const area = digits.slice(1, 4);
      if (AREA_ZONE.has(area)) return AREA_ZONE.get(area);
      const N = nanp();
      const region = N ? N.nanpRegion(digits) : 'US';
      if (region === 'CA') return 'America/Toronto';
      if (region === 'NANP') return 'America/Puerto_Rico';
      return US_WIDE;
    }
    const hit = DIAL_ZONES.find(([prefix]) => digits.startsWith(prefix));
    return hit ? hit[1] : null;
  }

  function expand(key) {
    return key === US_WIDE ? US_WIDE_ZONES.slice() : [key];
  }

  function localHour(date, timeZone) {
    const h = new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', hour: '2-digit' })
      .formatToParts(date).find(p => p.type === 'hour');
    return Number(h ? h.value : NaN) % 24;
  }

  /** True when it is night (22:00-08:00) in ANY of these zones at `date`. */
  function isQuietFor(date, zones) {
    return (zones || []).some((z) => {
      const h = localHour(date, z);
      return h >= QUIET_START_HOUR || h < QUIET_END_HOUR;
    });
  }

  function isDaytimeForAll(date, zones) {
    return (zones || []).every((z) => {
      const h = localHour(date, z);
      return h >= RELEASE_HOUR && h < QUIET_START_HOUR;
    });
  }

  /**
   * The first moment at or after `date` (on a quarter hour) when every zone is
   * between 09:00 and 22:00, within the next 24 hours; null when there is none.
   * For a single zone in its night that is 09:00 there.
   */
  function releaseAfter(date, zones) {
    if (!zones || !zones.length) return null;
    const from = Math.ceil(new Date(date).getTime() / STEP_MS) * STEP_MS;
    for (let t = from; t <= from + SEARCH_MS; t += STEP_MS) {
      const at = new Date(t);
      if (isDaytimeForAll(at, zones)) return at;
    }
    return null;
  }

  /**
   * The zones a send to these phones has to respect: every zone with at least
   * a quarter of the recognised numbers, as long as they share some daytime;
   * otherwise the largest alone. [] when no number is recognised.
   */
  function guestZones(phones, now = new Date()) {
    const counts = new Map();
    let total = 0;
    for (const phone of phones || []) {
      const key = zoneForPhone(phone);
      if (!key) continue;
      counts.set(key, (counts.get(key) || 0) + 1);
      total++;
    }
    if (!total) return [];
    const ranked = [...counts.entries()].sort((a, b) => b[1] - a[1]);
    const largest = expand(ranked[0][0]);
    const zones = [];
    for (const [key, n] of ranked) {
      if (n / total < SIGNIFICANT_SHARE) break;
      for (const z of expand(key)) if (!zones.includes(z)) zones.push(z);
    }
    return releaseAfter(now, zones) ? zones : largest;
  }

  const GuestTimezone = {
    QUIET_START_HOUR, QUIET_END_HOUR, RELEASE_HOUR, SIGNIFICANT_SHARE, US_WIDE, US_WIDE_ZONES,
    AREA_ZONE, DIAL_ZONES,
    zoneForPhone, guestZones, isQuietFor, releaseAfter, localHour
  };

  if (typeof window !== 'undefined') window.GuestTimezone = GuestTimezone;
  if (typeof module !== 'undefined' && module.exports) module.exports = GuestTimezone;
})();
